import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import moment from 'moment';
import { formatExtractedMoney, formatUtilityLabel } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
export const DASH = '—';
const DATE_FORMAT = 'MM/DD/YYYY';
const SHORT_DATE_FORMAT = 'MM/DD/YY';

/** Extraction payloads name the same value differently across providers, so try every known key. */
export function pickValue(source, keys) {
  if (!source) return null;
  const key = keys.find(k => source[k] !== undefined && source[k] !== null && source[k] !== '');
  return key ? source[key] : null;
}
export function formatDate(value) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_FORMAT;
  if (!value) return DASH;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : DASH;
}
export function formatShortDate(value) {
  return formatDate(value, SHORT_DATE_FORMAT);
}
export function sumAmounts(lineItems) {
  return (lineItems || []).reduce((acc, line) => {
    const amount = Number(line === null || line === void 0 ? void 0 : line.amount);
    return acc + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

/** Service total: the extracted subtotal when present, otherwise the sum of its line items. */
export function getServiceTotal(service) {
  const subtotal = Number(service === null || service === void 0 ? void 0 : service.subtotal);
  if (Number.isFinite(subtotal)) return subtotal;
  const lineItems = (service === null || service === void 0 ? void 0 : service.lineItems) || [];
  return lineItems.length ? sumAmounts(lineItems) : null;
}
export function formatMoney(amount, currency) {
  return amount === null || amount === undefined ? DASH : formatExtractedMoney(amount, currency);
}
export function getServiceUsage(service) {
  const usage = pickValue(service, ['usage', 'totalUsage', 'consumption', 'quantity']);
  if (usage === null) return null;
  const unit = pickValue(service, ['usageUnit', 'unit', 'uom']);
  const numeric = Number(usage);
  const value = Number.isFinite(numeric) ? numeric.toLocaleString('en-US') : String(usage);
  return unit ? "".concat(value, " ").concat(unit) : value;
}
export function getServiceDates(service, data) {
  var _pickValue, _pickValue2;
  const start = (_pickValue = pickValue(service, ['serviceStartDate', 'serviceStartDateOnly', 'startDate'])) !== null && _pickValue !== void 0 ? _pickValue : data === null || data === void 0 ? void 0 : data.serviceStartDate;
  const end = (_pickValue2 = pickValue(service, ['serviceEndDate', 'serviceEndDateOnly', 'endDate'])) !== null && _pickValue2 !== void 0 ? _pickValue2 : data === null || data === void 0 ? void 0 : data.serviceEndDate;
  return {
    end,
    start
  };
}
export function formatServicePeriod(start, end) {
  let format = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : SHORT_DATE_FORMAT;
  const from = moment(start);
  const to = moment(end);
  if (!start || !end || !from.isValid() || !to.isValid()) return DASH;
  return "".concat(from.format(format), " \u2013 ").concat(to.format(format));
}

/**
 * The one period the given services all cover, so it can be edited as a single range. A bill whose
 * utilities are billed over different periods has no such date, and says so by leaving it empty
 * rather than by proposing one of them.
 */
export function getCommonServiceDates(services, data) {
  const dates = (services || []).map(service => getServiceDates(service, data));
  if (!dates.length) return {
    end: (data === null || data === void 0 ? void 0 : data.serviceEndDate) || '',
    start: (data === null || data === void 0 ? void 0 : data.serviceStartDate) || ''
  };
  const shared = key => {
    const values = new Set(dates.map(date => date[key] || ''));
    return values.size === 1 ? [...values][0] : '';
  };
  return {
    end: shared('end'),
    start: shared('start')
  };
}

/**
 * The periods the given services cover, one entry per distinct range and named after the utilities
 * charged over it. Most bills cover one period for everything, but a bill that does not would be
 * misstated by a single merged range.
 */
export function getServicePeriods(services, data) {
  const byPeriod = new Map();
  (services || []).forEach(service => {
    const _getServiceDates = getServiceDates(service, data),
      end = _getServiceDates.end,
      start = _getServiceDates.start;
    const period = formatServicePeriod(start, end, DATE_FORMAT);
    if (period === DASH) return;
    byPeriod.set(period, [...(byPeriod.get(period) || []), formatUtilityLabel(service.serviceType)]);
  });
  return [...byPeriod.entries()].map(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      period = _ref2[0],
      utilities = _ref2[1];
    return {
      period,
      utilities: utilities.join(', ')
    };
  });
}